import { Platform, PlatformDetectionResult } from '@/types'

// Import mapping configurations
import boltMappings from '@/config/columnMappings/boltMappings.json'
import uberMappings from '@/config/columnMappings/uberMappings.json'
import sumupMappings from '@/config/columnMappings/sumupMappings.json'
import genericMappings from '@/config/columnMappings/genericMappings.json'

/**
 * Platform Detection Service
 *
 * Responsible for identifying the platform (Bolt, Uber, SumUp, etc.) based on CSV headers.
 * This is the first step in the new cleaner architecture.
 */

// Configuration type for mappings
export interface ColumnMappingConfig {
  platform: string
  identifierFields: Record<string, string[]>
  earningsFields: Record<string, string[]>
  requiredFields: {
    identifierFields: string[]
    earningsFields: string[]
  }
  optionalFields: {
    identifierFields: string[]
    earningsFields: string[]
  }
}

export interface NormalizedHeader {
  original: string
  normalized: string
  tokens: string[]
}

export class PlatformDetectionService {
  private static mappingConfigs: Record<string, ColumnMappingConfig> = {
    bolt: boltMappings as ColumnMappingConfig,
    uber: uberMappings as ColumnMappingConfig,
    sumup: sumupMappings as ColumnMappingConfig,
    generic: genericMappings as ColumnMappingConfig
  }

  /**
   * Detect platform based on CSV headers
   */
  static detectPlatformFromFile(headers: string[]): PlatformDetectionResult {
    const HIGH_CONFIDENCE_THRESHOLD = 80
    const MEDIUM_CONFIDENCE_THRESHOLD = 60

    const platformMatches: Array<{
      platform: string
      confidence: number
      reason: string
    }> = []

    // Test each platform configuration
    for (const [platform, config] of Object.entries(this.mappingConfigs)) {
      if (platform === 'generic') continue // Skip generic platform for pure detection

      const platformScore = this.calculatePlatformScore(headers, config)

      if (platformScore.confidence > 0) {
        platformMatches.push({
          platform,
          confidence: platformScore.confidence,
          reason: platformScore.reason
        })
      }
    }

    // Sort by confidence
    platformMatches.sort((a, b) => b.confidence - a.confidence)

    const bestMatch = platformMatches[0]
    let detectedPlatform = bestMatch?.platform || null
    const confidence = bestMatch?.confidence || 0

    // Handle edge cases
    if (confidence < MEDIUM_CONFIDENCE_THRESHOLD) {
      console.log(`[PlatformDetection] Low confidence (${confidence}%), using fallback logic`)

      // Fallback: check for very specific indicators
      const hasSpecificIndicators = this.checkSpecificPlatformIndicators(headers)
      if (hasSpecificIndicators) {
        detectedPlatform = hasSpecificIndicators
      } else {
        // Default to 'other' for unknown formats
        detectedPlatform = 'other'
      }
    }

    // Auto-select logic
    let suggestedDefault = confidence >= MEDIUM_CONFIDENCE_THRESHOLD && detectedPlatform ? detectedPlatform : undefined
    const shouldAutoSelect = confidence >= HIGH_CONFIDENCE_THRESHOLD

    if (!suggestedDefault && detectedPlatform === 'other') {
      suggestedDefault = 'other'
    }

    return {
      detectedPlatform,
      confidence,
      allMatches: platformMatches,
      shouldAutoSelect,
      suggestedDefault: suggestedDefault || null
    }
  }

  /**
   * Calculate platform score based on how well headers match the configuration
   */
  private static calculatePlatformScore(
    headers: string[],
    config: ColumnMappingConfig
  ): { confidence: number; reason: string } {
    const normalizedHeaders = headers.map(h => this.normalizeHeader(h))
    let score = 0
    let maxScore = 0
    const matchedFields: string[] = []

    // Check identifier fields
    for (const [fieldKey, synonyms] of Object.entries(config.identifierFields)) {
      const isRequired = config.requiredFields.identifierFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxScore += weight

      const bestMatch = this.findBestHeaderMatch(normalizedHeaders, synonyms)
      if (bestMatch.confidence >= 0.6) {
        score += weight * bestMatch.confidence
        matchedFields.push(fieldKey)
      }
    }

    // Check earnings fields
    for (const [fieldKey, synonyms] of Object.entries(config.earningsFields)) {
      const isRequired = config.requiredFields.earningsFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxScore += weight

      const bestMatch = this.findBestHeaderMatch(normalizedHeaders, synonyms)
      if (bestMatch.confidence >= 0.6) {
        score += weight * bestMatch.confidence
        matchedFields.push(fieldKey)
      }
    }

    const confidence = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0
    const reason = `Matched ${matchedFields.length} fields: ${matchedFields.join(', ')}`

    return { confidence, reason }
  }

  /**
   * Find best match for a field's synonyms against normalized headers
   */
  private static findBestHeaderMatch(
    normalizedHeaders: NormalizedHeader[],
    synonyms: string[]
  ): { confidence: number; reason: string } {
    let bestConfidence = 0
    let bestReason = ''

    for (const header of normalizedHeaders) {
      for (const synonym of synonyms) {
        const normalizedSynonym = this.normalizeHeader(synonym)

        // Exact match
        if (header.normalized === normalizedSynonym.normalized) {
          return { confidence: 1.0, reason: `Exact match: "${synonym}"` }
        }

        // Similarity match
        const similarity = this.calculateSimilarity(header.normalized, normalizedSynonym.normalized)
        if (similarity > bestConfidence && similarity > 0.7) {
          bestConfidence = similarity
          bestReason = `Similar to: "${synonym}" (${Math.round(similarity * 100)}%)`
        }

        // Token match
        const tokenMatch = this.calculateTokenMatch(header.tokens, normalizedSynonym.tokens)
        if (tokenMatch > bestConfidence && tokenMatch > 0.6) {
          bestConfidence = tokenMatch
          bestReason = `Token match: "${synonym}" (${Math.round(tokenMatch * 100)}%)`
        }
      }
    }

    return { confidence: bestConfidence, reason: bestReason }
  }

  /**
   * Check for specific platform indicators as fallback
   */
  private static checkSpecificPlatformIndicators(headers: string[]): string | null {
    const headerStr = headers.join(' ').toLowerCase()

    // Bolt-specific indicators
    if (headerStr.includes('bolt') || headerStr.includes('taxify')) {
      return 'bolt'
    }

    // Uber-specific indicators
    if (headerStr.includes('uber') || headerStr.includes('driver_uuid')) {
      return 'uber'
    }

    // SumUp-specific indicators
    if (headerStr.includes('sumup') || headerStr.includes('merchant')) {
      return 'sumup'
    }

    return null
  }

  /**
   * Normalize header names for better matching
   */
  private static normalizeHeader(header: string): NormalizedHeader {
    // Basic cleaning
    let normalized = header.toLowerCase()
      .trim()
      .replace(/[_\-\s]+/g, ' ')
      .replace(/[^\w\såäö]/g, '')
      .trim()

    // Remove accents/diacritics
    normalized = normalized
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ýÿ]/g, 'y')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')

    // Create tokens for partial matching
    const tokens = normalized.split(' ').filter(token => token.length > 0)

    return {
      original: header,
      normalized,
      tokens
    }
  }

  /**
   * Calculate similarity using Levenshtein distance
   */
  private static calculateSimilarity(str1: string, str2: string): number {
    if (str1 === str2) return 1.0

    const maxLength = Math.max(str1.length, str2.length)
    if (maxLength === 0) return 1.0

    const distance = this.levenshteinDistance(str1, str2)
    return Math.max(0, 1 - (distance / maxLength))
  }

  /**
   * Calculate Levenshtein distance
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = []

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          )
        }
      }
    }

    return matrix[str2.length][str1.length]
  }

  /**
   * Calculate token-based matching score
   */
  private static calculateTokenMatch(tokens1: string[], tokens2: string[]): number {
    if (tokens1.length === 0 && tokens2.length === 0) return 1.0
    if (tokens1.length === 0 || tokens2.length === 0) return 0.0

    let matches = 0
    const maxTokens = Math.max(tokens1.length, tokens2.length)

    for (const token1 of tokens1) {
      for (const token2 of tokens2) {
        if (token1 === token2 || this.calculateSimilarity(token1, token2) > 0.8) {
          matches++
          break
        }
      }
    }

    return matches / maxTokens
  }
}